import {inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {DirectusService, TextTemplate} from '@frontend/directus';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {formatDateToDDMMYYYY} from '../utils/date-utils';
import {
  ArchitecturalPrincipleControllerService,
  ArchitecturalPrincipleResponse,
  DesignCriteriaControllerService,
  DesignCriteriaResponse,
  ProjectCharterControllerService,
  ProjectCharterResponse,
  ProjectTimeFrameResponse,
  ProviderTenderControllerService,
} from '@frontend/shared-api';
import {Step12ProjectCharterService} from '../../pages/customer/project-sequence/chapter/chapter-1-project-setup/step-1-2-project-charter/step-1-2-project-charter.service';
import {CHARTER_TABS, CharterData, CharterSection, CharterTab} from '../models/charter.types';

export interface EmployeeData {
  industrialEmployees?: number;
  salariedEmployees?: number;
}

@Injectable({
  providedIn: 'root',
})
export class RfiProjectDescriptionFormatterService {
  private readonly transloco = inject(TranslocoService);
  private readonly directusService = inject(DirectusService);
  private readonly charterService = inject(Step12ProjectCharterService);
  private readonly projectCharterService = inject(ProjectCharterControllerService);
  private readonly designCriteriaService = inject(DesignCriteriaControllerService);
  private readonly architecturalPrincipleService = inject(ArchitecturalPrincipleControllerService);
  private readonly providerTenderApi = inject(ProviderTenderControllerService);

  formatCompleteContent(projectId: number, employeeData?: EmployeeData): Observable<string> {
    return this.fetchCharterDataForCustomer(projectId).pipe(
      switchMap((charterData) => this.buildFormattedContent(charterData, employeeData)),
      catchError(() => of('')),
    );
  }

  formatCompleteContentForProvider(
    tenderId: number,
    employeeData?: EmployeeData,
  ): Observable<string> {
    return this.fetchTenderRfiDescriptionForProvider(tenderId).pipe(
      switchMap((charterData) => this.buildFormattedContent(charterData, employeeData)),
      catchError(() => of('')),
    );
  }

  private fetchCharterDataForCustomer(projectId: number): Observable<CharterData> {
    return forkJoin({
      overview: this.charterService.getCharterOverview(projectId),
      scope: this.charterService.getCharterScope(projectId),
      parameters: this.charterService.getCharterParameters(projectId),
      rolesAndStakeholders: this.charterService.getCharterRolesAndStakeholders(projectId),
      charter: this.projectCharterService.getProjectCharter({projectId}),
      designCriteria: this.designCriteriaService.getDesignCriteriaByProjectId({projectId}),
      architecturalPrinciples: this.architecturalPrincipleService.getByProject({projectId}),
    }).pipe(
      map((data) => ({
        overview: data.overview,
        scope: data.scope,
        parameters: data.parameters,
        rolesAndStakeholders: data.rolesAndStakeholders,
        timeFrames: data.charter.projectTimeFrames ?? [],
        designCriteria: data.designCriteria,
        architecturalPrinciples: data.architecturalPrinciples,
      })),
    );
  }

  private fetchTenderRfiDescriptionForProvider(tenderId: number): Observable<CharterData> {
    return this.providerTenderApi
      .getTenderRfiDescription({tenderId})
      .pipe(
        map((response) =>
          this.mapTenderRfiDescriptionToSections(
            response.projectCharter ?? {projectId: 0, projectTimeFrames: []},
            response.designCriteria ?? [],
            response.architecturalPrinciples ?? [],
          ),
        ),
      );
  }

  private mapTenderRfiDescriptionToSections(
    charter: ProjectCharterResponse,
    designCriteria: DesignCriteriaResponse[],
    architecturalPrinciples: ArchitecturalPrincipleResponse[],
  ): CharterData {
    return {
      overview: {
        projectId: charter.projectId,
        companyProfile: charter.companyProfile,
        companyCoreProducts: charter.companyCoreProducts,
        companiesLongTermGoals: charter.companiesLongTermGoals,
        projectRationale: charter.projectRationale,
        projectDescription: charter.projectDescription,
        companyValue1: charter.companyValue1,
        companyValue2: charter.companyValue2,
        companyValue3: charter.companyValue3,
      },
      scope: {
        projectId: charter.projectId,
        inScope: charter.inScope,
        outOfScope: charter.outOfScope,
      },
      parameters: {
        projectId: charter.projectId,
        projectGoals: charter.projectGoals,
        deliverables: charter.deliverables,
        potentialRisks: charter.potentialRisks,
        reasonsForNewHrSystem: charter.reasonsForNewHrSystem,
      },
      rolesAndStakeholders: {
        projectId: charter.projectId,
        projectRolesAndResponsibilities: charter.projectRolesAndResponsibilities,
        keyStakeholders: charter.keyStakeholders,
      },
      timeFrames: charter.projectTimeFrames ?? [],
      designCriteria,
      architecturalPrinciples,
    };
  }

  private buildFormattedContent(
    charterData: CharterData,
    employeeData?: EmployeeData,
  ): Observable<string> {
    const tabsWithData = this.mapCharterDataToTabs(charterData);
    const sectionKeys = this.extractSectionKeys(tabsWithData);
    const includedDesignCriteria = charterData.designCriteria.filter(
      (criterion) => criterion.included,
    );

    const companyValueIds = [
      charterData.overview.companyValue1,
      charterData.overview.companyValue2,
      charterData.overview.companyValue3,
    ].filter((id): id is string => !!id);

    return forkJoin({
      templates: this.loadTemplates(sectionKeys),
      companyValues: this.loadCompanyValues(companyValueIds),
      designCriteriaNameById: this.loadDesignCriteriaNameByIdMap(includedDesignCriteria),
      architecturalPrincipleName: this.loadArchitecturalPrincipleName(
        charterData.architecturalPrinciples,
      ),
    }).pipe(
      map(({templates, companyValues, designCriteriaNameById, architecturalPrincipleName}) =>
        this.renderHtmlContent(
          tabsWithData,
          templates,
          charterData.timeFrames,
          includedDesignCriteria,
          designCriteriaNameById,
          architecturalPrincipleName,
          employeeData,
          companyValues,
        ),
      ),
    );
  }

  private mapCharterDataToTabs(charterData: CharterData): CharterTab[] {
    return [
      {...CHARTER_TABS[0], data: charterData.overview},
      {...CHARTER_TABS[1], data: charterData.scope},
      {...CHARTER_TABS[2], data: charterData.parameters},
      {...CHARTER_TABS[3], data: charterData.rolesAndStakeholders},
    ];
  }

  private extractSectionKeys(tabs: CharterTab[]): string[] {
    return tabs.flatMap((tab) => tab.sections.map((section) => section.key));
  }

  private loadTemplates(sectionKeys: string[]): Observable<Array<TextTemplate | null>> {
    const templateRequests = sectionKeys.map((key) =>
      this.directusService.loadTextTemplate(key).pipe(catchError(() => of(null))),
    );
    return forkJoin(templateRequests);
  }

  private loadCompanyValues(companyValueIds: string[]): Observable<string[]> {
    if (companyValueIds.length === 0) {
      return of([]);
    }

    return this.directusService.loadDropdownWithValues('project_value').pipe(
      map((dropdown) => {
        const values: string[] = [];
        companyValueIds.forEach((id) => {
          const match = dropdown.values?.find((v) => v.id === id);
          if (match?.translations) {
            const activeLang = this.transloco.getActiveLang();
            const translation = match.translations.find((t) => t.language === activeLang);
            if (translation?.name) {
              values.push(translation.name);
            }
          }
        });
        return values;
      }),
      catchError(() => of([])),
    );
  }

  private renderHtmlContent(
    tabs: CharterTab[],
    templates: Array<TextTemplate | null>,
    timeFrames: ProjectTimeFrameResponse[],
    designCriteria: DesignCriteriaResponse[],
    designCriteriaNameByIdMap: Map<string, string>,
    architecturalPrincipleName: string | undefined,
    employeeData?: EmployeeData,
    companyValues?: string[],
  ): string {
    const employeeSection = this.renderEmployeeSection(employeeData, companyValues);
    const sections = this.renderChapterSections(tabs, templates);
    const timeFramesHtml = this.renderTimeFramesSection(timeFrames);
    const designCriteriaHtml = this.renderDesignCriteriaSection(
      designCriteria,
      designCriteriaNameByIdMap,
    );
    const architecturalPrincipleHtml = this.renderArchitecturalPrincipleSection(
      architecturalPrincipleName,
    );

    return `${employeeSection}${sections}${timeFramesHtml}${designCriteriaHtml}${architecturalPrincipleHtml}`;
  }

  private renderEmployeeSection(employeeData?: EmployeeData, companyValues?: string[]): string {
    if (!employeeData || (!employeeData.industrialEmployees && !employeeData.salariedEmployees)) {
      return '';
    }

    const headcountGroupTitle = this.transloco.translate('project.steps.headcountGroup');
    const industrialLabel = this.transloco.translate('project.steps.industrialEmployees');
    const salariedLabel = this.transloco.translate('project.steps.salariedEmployees');
    const totalLabel = this.transloco.translate('project.steps.totalHc');

    const industrial = employeeData.industrialEmployees ?? 0;
    const salaried = employeeData.salariedEmployees ?? 0;
    const total = industrial + salaried;

    const companyValuesSection = this.renderCompanyValuesSection(companyValues);

    return `
      <div class="mb-6">
        <h3 class="text-lg font-semibold mb-4">${headcountGroupTitle}</h3>
        <div class="flex gap-8">
          <div>
            <span class="font-semibold">${industrialLabel}:</span>
            <span class="ml-2">${industrial}</span>
          </div>
          <div>
            <span class="font-semibold">${salariedLabel}:</span>
            <span class="ml-2">${salaried}</span>
          </div>
          <div>
            <span class="font-semibold">${totalLabel}:</span>
            <span class="ml-2">${total}</span>
          </div>
        </div>
      </div>
      ${companyValuesSection}
      <hr class="my-6 border-surface">
    `;
  }

  private renderCompanyValuesSection(companyValues?: string[]): string {
    if (!companyValues || companyValues.length === 0) {
      return '';
    }

    const dropdownSelectionsTitle = this.transloco.translate(
      'project.steps.projectChart.dropdownSelections',
    );

    const valuesList = companyValues
      .map((value, index) => `<li>${index + 1}. ${value}</li>`)
      .join('');

    return `
      <div class="mb-6">
        <h3 class="text-lg font-semibold mb-4">${dropdownSelectionsTitle}</h3>
        <ul class="list-none pl-0">
          ${valuesList}
        </ul>
      </div>
    `;
  }

  private renderChapterSections(tabs: CharterTab[], templates: Array<TextTemplate | null>): string {
    let content = '';
    let templateIndex = 0;

    tabs.forEach((tab, tabIndex) => {
      const tabHeader = this.transloco.translate(tab.tabKey);
      content += `<h3 class="text-lg font-semibold mb-4">${tabHeader}</h3>`;

      tab.sections.forEach((section) => {
        const template = templates[templateIndex++];
        const sectionContent = tab.data?.[section.fieldName as keyof typeof tab.data] as
          string | undefined;

        if (this.hasSectionContent(sectionContent)) {
          content += this.renderSection(template, section, sectionContent);
        }
      });

      if (tabIndex < tabs.length - 1) {
        content += '<hr class="my-4 border-surface">';
      }
    });

    return content;
  }

  private hasSectionContent(content: string | undefined): content is string {
    return Boolean(content?.trim());
  }

  private renderSection(
    template: TextTemplate | null,
    section: CharterSection,
    content: string,
  ): string {
    const sectionHeader = this.getSectionHeader(template, section.tooltipKey);
    return `<h4 class="font-semibold mb-1">${sectionHeader}</h4><div class="mb-4">${content}</div>`;
  }

  private getSectionHeader(template: TextTemplate | null, fallbackKey: string): string {
    if (template) {
      const activeLang = this.transloco.getActiveLang();
      const match = template.translations?.find((t) => t.language === activeLang);
      if (match?.name) {
        return match.name;
      }
    }
    return this.transloco.translate(fallbackKey);
  }

  private renderTimeFramesSection(timeFrames: ProjectTimeFrameResponse[]): string {
    if (!timeFrames.length) {
      return '';
    }

    const header = this.transloco.translate('project.steps.projectChartTab5');
    const timeFramesHtml = timeFrames
      .map((timeframe, index) => this.renderTimeFrame(timeframe, index))
      .join('');

    return `<hr class="my-4 border-surface"><h3 class="text-lg font-semibold mb-4">${header}</h3>${timeFramesHtml}`;
  }

  private renderTimeFrame(timeframe: ProjectTimeFrameResponse, index: number): string {
    const startDateLabel = this.transloco.translate('common.startDate');
    const endDateLabel = this.transloco.translate('common.endDate');
    const formattedStartDate = formatDateToDDMMYYYY(timeframe.startDate);
    const formattedEndDate = formatDateToDDMMYYYY(timeframe.estimatedEndDate);

    const milestoneText = `${index + 1}. ${timeframe.milestone} - ${startDateLabel}: ${formattedStartDate} - ${endDateLabel}: ${formattedEndDate}`;
    const description = timeframe.stageDescription
      ? `<p class="mb-4">${timeframe.stageDescription}</p>`
      : '';

    return `<h4 class="font-semibold mb-1">${milestoneText}</h4>${description}`;
  }

  private loadDesignCriteriaNameByIdMap(
    designCriteria: DesignCriteriaResponse[],
  ): Observable<Map<string, string>> {
    const directusIds = designCriteria
      .map((criterion) => criterion.directusId)
      .filter((id): id is string => Boolean(id));

    if (!directusIds.length) {
      return of(new Map<string, string>());
    }

    return this.directusService.loadDesignCriteria().pipe(
      map((directusCriteria) => {
        const activeLang = this.transloco.getActiveLang();
        const criteriaById = new Map<string, string>();

        directusCriteria.forEach((criterion) => {
          const translatedName =
            criterion.translations?.find((translation) => translation.language === activeLang)
              ?.name ??
            criterion.translations?.[0]?.name ??
            criterion.title;

          if (translatedName) {
            criteriaById.set(criterion.id, translatedName);
          }
        });

        return criteriaById;
      }),
      catchError(() => of(new Map<string, string>())),
    );
  }

  private loadArchitecturalPrincipleName(
    architecturalPrinciples: ArchitecturalPrincipleResponse[],
  ): Observable<string | undefined> {
    const selectedArchitecturalPrincipleId = architecturalPrinciples[0]?.name;
    if (!selectedArchitecturalPrincipleId) {
      return of(undefined);
    }

    return this.directusService.loadArchitecturalPrinciples().pipe(
      map((principles) => {
        const activeLang = this.transloco.getActiveLang();
        const selected = principles.find(
          (principle) => principle.id === selectedArchitecturalPrincipleId,
        );
        return (
          selected?.translations.find((translation) => translation.language === activeLang)?.name ??
          selected?.translations[0]?.name
        );
      }),
      catchError(() => of(undefined)),
    );
  }

  private renderDesignCriteriaSection(
    designCriteria: DesignCriteriaResponse[],
    designCriteriaNameByIdMap: Map<string, string>,
  ): string {
    if (!designCriteria.length) {
      return '';
    }

    const sectionTitle = this.transloco.translate('project.steps.designCriteria');
    const criteriaNames = designCriteria
      .map((criterion) => this.getDesignCriteriaName(criterion, designCriteriaNameByIdMap))
      .filter((name): name is string => Boolean(name?.trim()));
    const criteriaItems = criteriaNames
      .map((name, index) => `<li>${index + 1}. ${name}</li>`)
      .join('');

    if (!criteriaItems) {
      return '';
    }

    return `<hr class="my-4 border-surface"><h3 class="text-lg font-semibold mb-4">${sectionTitle}</h3><ul class="list-none pl-0">${criteriaItems}</ul>`;
  }

  private getDesignCriteriaName(
    criterion: DesignCriteriaResponse,
    designCriteriaNameByIdMap: Map<string, string>,
  ): string | undefined {
    return (
      (criterion.directusId ? designCriteriaNameByIdMap.get(criterion.directusId) : undefined) ??
      criterion.name
    );
  }

  private renderArchitecturalPrincipleSection(
    architecturalPrincipleName: string | undefined,
  ): string {
    if (!architecturalPrincipleName?.trim()) {
      return '';
    }

    const sectionTitle = this.transloco.translate('project.steps.architecturalPrinciple');
    return `<hr class="my-4 border-surface"><h3 class="text-lg font-semibold mb-4">${sectionTitle}</h3><p class="mb-4">${architecturalPrincipleName}</p>`;
  }
}
